import type { AwaitingRow } from './awaitingSplit'
import type { Scope } from './ui'

export const TIME_ADMIN_ABILITIES = ['time.admin']

export const BACKLOG_TAB_HREF = '/time?scope=backlog'

export const LEAVE_ADMIN_ABILITY = 'leave.admin'

/** The scope tabs offered; `team` and `all` stay recognised from old links but are never offered. */
export function offeredScopes(isAdmin: boolean): Scope[] {
  return isAdmin ? ['own', 'awaiting', 'admin'] : ['own', 'awaiting']
}

/** An old `?scope=backlog` link opens the tab that now lists those decisions: Everyone for an admin, else Supervisees. */
export function resolveRequestedScope(requested: string | null, isAdmin: boolean): string | null {
  if (requested !== 'backlog') return requested
  return isAdmin ? 'admin' : 'awaiting'
}

/** The Everyone tab badges every pending person (`/days/awaiting?scope=all`); every other tab and the counts stay team-only. */
export function awaitingBadgeScope(scope: Scope): 'all' | 'team' {
  return scope === 'admin' || scope === 'all' ? 'all' : 'team'
}

const TAB_ORDER: Scope[] = ['own', 'awaiting', 'admin']

/** The tabs to render: the known scopes in tab order; stale `team`/`all` links resolve but never get a tab. */
export function scopeTabs(scopes: Scope[]): Scope[] {
  return TAB_ORDER.filter((s) => scopes.includes(s))
}

/** The Everyone tab badge: distinct listed people with a pending day; undefined while the rows are unknown, never a 0 from nothing. */
export function pendingPeopleCount(
  rows: Pick<AwaitingRow, 'employee_id' | 'pending_count'>[] | undefined,
  listed: (id: string) => boolean,
): number | undefined {
  if (!rows) return undefined
  return new Set(rows.filter((r) => r.pending_count > 0 && listed(r.employee_id)).map((r) => r.employee_id)).size
}

export const PERSON_LINK_KEY = 'person'

/** The tab a `?person=` link opens: a tab whose own list holds the person, else the caller's own scope with nobody picked. */
export function personLinkTarget(
  personId: string,
  ctx: { meId: string; awaitingIds: ReadonlySet<string>; adminIds: ReadonlySet<string> | null },
): { scope: Scope; personId: string | null } {
  if (personId !== ctx.meId) {
    if (ctx.awaitingIds.has(personId)) return { scope: 'awaiting', personId }
    if (ctx.adminIds?.has(personId)) return { scope: 'admin', personId }
  }
  return { scope: 'own', personId: null }
}
